/**
 * Consulta dos recebidos: o acervo e as jornadas que outras marcas
 * concederam à marca da sessão (A156).
 *
 * As duas telas "Recebidos" listavam tudo de uma vez, numa tabela por
 * origem: com o acervo completo da Eva concedido, eram centenas de linhas.
 * Agora a tela pede uma página, com filtro, e a API filtra, conta e pagina.
 * O contrato mora aqui para a API validar e o painel montar a mesma
 * pergunta.
 */

import { z } from 'zod';

/** Na URL tudo chega como texto; vazio é "sem filtro". */
const textoOpcional = z.preprocess((v) => (typeof v === 'string' && v.trim() === '' ? undefined : v), z.string().trim().max(120).optional());

/** Situação para as pacientes da marca: o que ela mostra e o que ocultou. */
export const SituacaoDoRecebidoEnum = z.enum(['TODOS', 'VISIVEIS', 'OCULTOS']);
export type SituacaoDoRecebido = z.infer<typeof SituacaoDoRecebidoEnum>;

/** Barreira de conteúdo: só faz sentido para marca com barreira ativa. */
export const BarreiraDoRecebidoEnum = z.enum(['TODOS', 'LIVRES', 'TRAVADOS']);
export type BarreiraDoRecebido = z.infer<typeof BarreiraDoRecebidoEnum>;

export const OrdemDoRecebidoEnum = z.enum(['TITULO', 'RECENTES']);
export type OrdemDoRecebido = z.infer<typeof OrdemDoRecebidoEnum>;

export const POR_PAGINA_DOS_RECEBIDOS = [24, 48, 96] as const;

/** Os filtros, sem a página: é o que a ação em massa reaproveita. */
const FiltroBase = {
  busca: textoOpcional,
  /** Id da marca de origem. */
  origem: textoOpcional,
  /** Vídeo: subtipo (COURSE, PODCAST…); revista: magazineType; jornada: a jornada. */
  categoria: textoOpcional,
  situacao: SituacaoDoRecebidoEnum.default('TODOS'),
};

const Paginacao = {
  ordem: OrdemDoRecebidoEnum.default('TITULO'),
  pagina: z.coerce.number().int().min(1).default(1),
  porPagina: z.coerce.number().int().min(1).max(96).default(24),
};

// --- Acervo (Conteúdo → Recebidos) ---

export const TipoDoAcervoRecebidoEnum = z.enum(['VIDEO', 'MAGAZINE', 'TRAIL']);
export type TipoDoAcervoRecebido = z.infer<typeof TipoDoAcervoRecebidoEnum>;

export const FiltroDoAcervoRecebidoSchema = z.object({
  tipo: TipoDoAcervoRecebidoEnum.default('VIDEO'),
  ...FiltroBase,
  barreira: BarreiraDoRecebidoEnum.default('TODOS'),
});
export type FiltroDoAcervoRecebido = z.infer<typeof FiltroDoAcervoRecebidoSchema>;

export const ConsultaDoAcervoRecebidoSchema = FiltroDoAcervoRecebidoSchema.extend(Paginacao);
export type ConsultaDoAcervoRecebido = z.infer<typeof ConsultaDoAcervoRecebidoSchema>;

// --- Jornadas (Jornadas → Recebidas) ---

export const TipoDeJornadaRecebidaEnum = z.enum(['CHECKPOINT', 'REMINDER']);

export const FiltroDeJornadasRecebidasSchema = z.object({
  tipo: TipoDeJornadaRecebidaEnum.default('CHECKPOINT'),
  ...FiltroBase,
});
export type FiltroDeJornadasRecebidas = z.infer<typeof FiltroDeJornadasRecebidasSchema>;

export const ConsultaDeJornadasRecebidasSchema = FiltroDeJornadasRecebidasSchema.extend(Paginacao);
export type ConsultaDeJornadasRecebidas = z.infer<typeof ConsultaDeJornadasRecebidasSchema>;

// --- Ação em massa ---

/**
 * Sobre o que a ação em massa age: os itens marcados, ou tudo o que casa
 * com um filtro (o "selecionar todos os 230 deste filtro", que passa de uma
 * página). Ids de fora do que a marca recebe são ignorados pela API.
 */
export const alvoEmMassa = <F extends z.ZodTypeAny>(filtro: F) =>
  z.union([
    z.object({ ids: z.array(z.string().min(1)).min(1).max(1000) }),
    z.object({ filtro }),
  ]);

export const CuradoriaEmMassaDoAcervoSchema = z.object({
  alvo: alvoEmMassa(FiltroDoAcervoRecebidoSchema),
  patch: z
    .object({ oculto: z.boolean().optional(), destravado: z.boolean().optional() })
    .refine((p) => p.oculto !== undefined || p.destravado !== undefined, 'Diga o que mudar: oculto ou destravado.'),
});
export type CuradoriaEmMassaDoAcervo = z.infer<typeof CuradoriaEmMassaDoAcervoSchema>;

export const OcultarJornadasEmMassaSchema = z.object({
  alvo: alvoEmMassa(FiltroDeJornadasRecebidasSchema),
  oculto: z.boolean(),
});
export type OcultarJornadasEmMassa = z.infer<typeof OcultarJornadasEmMassaSchema>;

// --- Resposta ---

export interface OrigemDoRecebido {
  id: string;
  name: string;
  logoUrl: string | null;
  /** O que a concessão cobre: o acervo inteiro ou itens escolhidos. */
  escopo: 'ALL' | 'SELECTED';
  /** Itens desta origem, com os filtros de agora (menos o de origem). */
  total: number;
}

export interface PaginaDeRecebidos<T> {
  itens: T[];
  total: number;
  pagina: number;
  porPagina: number;
  totalDePaginas: number;
}

/** Contagens para a tela: quantos há em cada guia, categoria e situação. */
export interface FacetasDeRecebidos<Tipo extends string> {
  /** Por guia, com os filtros de agora (menos tipo e categoria). */
  porTipo: Record<Tipo, number>;
  /** Na guia aberta, por categoria (menos o filtro de categoria). */
  porCategoria: Array<{ valor: string; total: number }>;
  /** Na guia aberta, visíveis e ocultos (menos o filtro de situação). */
  porSituacao: { visiveis: number; ocultos: number };
}

/**
 * Busca sem acento e sem caixa: "gestacao" acha "Gestação". Os títulos são
 * escritos por gente, e quem busca não digita acento.
 */
export function normalizarParaBusca(texto: string): string {
  return texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}
